/**
 * The CI fleet's alerts, shown in the portal only. org-admin#97.
 *
 * A banner on the CI fleet screen and a count on its nav item. Not phone, not
 * email: Jon deferred push on 11 Sep, and nothing automated sends mail.
 *
 * ── AN ALERT THAT FIRES ON THE NORMAL STATE IS WORSE THAN NONE ───────
 *
 * The 12 Sep comment on #97. As first written, "a runner offline for 10 minutes
 * while its box reports" would have fired all afternoon on six healthy runners,
 * because GitHub reports them offline while they work (actions/runner#3892). So
 * nothing here reads GitHub's runner status. The runner alert comes from the
 * box's own report: its service running with no connection to GitHub, lasting.
 *
 * ── NO BLACKSMITH ALERT ──────────────────────────────────────────────
 *
 * The first version warned when Blacksmith was projected past its trip line.
 * On its first day it fired on a fleet with nothing wrong: September had passed
 * 2,800 before CI moved to your runners, so it would have stayed on all month.
 * Nothing needs doing when it is true, because routing already sends CI to
 * GitHub-hosted rather than Blacksmith past the line, and the Routing tab's
 * meter shows the minutes. Jon removed it, 14 Sep.
 *
 * ── THE UPS (fleet-api#59) ───────────────────────────────────────────
 *
 * On battery and low battery come from the UPS's own flags, read by the box
 * its USB cable is in. Stale is the UPS's silence, separately from that box's:
 * a box can keep reporting while its NUT driver has died, and then nothing
 * would say the power went. A UPS last heard on battery that goes quiet is the
 * boxes shutting down, so it is a fault, not a warning.
 *
 * Pure, and computed from what the portal already fetches, so every rule is
 * tested without a clock or a network.
 */
import type { Boxes, Queue } from './fleet-api.js';
import { CPU_HOT_C, DISK_WARN_SHARE, durationText, hasDocker, runnerName, share } from './fleet-boxes.js';
import { pct, shutdownLine } from './fleet-ups.js';
import { count } from './format.js';

/** A box silent this long is an alert. The card calls it silent from fleet-api's 150 s. */
export const SILENT_ALERT_S = 5 * 60;
/**
 * A runner with no GitHub connection is an alert once it has also logged
 * nothing for this long. A runner registering between jobs holds no connection
 * for a few seconds, straight after a log line, so it never reaches this.
 */
export const NO_CONNECTION_ALERT_S = 10 * 60;

export interface FleetAlert {
  /** Stable across polls, for React keys. */
  key: string;
  tone: 'danger' | 'warn';
  title: string;
  detail: string;
}

export interface AlertInputs {
  boxes?: Boxes | null;
  queue?: Queue | null;
  now?: number;
}

export function fleetAlerts({ boxes, queue, now = Date.now() }: AlertInputs): FleetAlert[] {
  const alerts: FleetAlert[] = [];

  for (const box of boxes?.boxes ?? []) {
    if (box.state === 'silent' && (box.since_report_s ?? 0) >= SILENT_ALERT_S) {
      alerts.push({
        key: `silent:${box.name}`,
        tone: 'danger',
        title: `${box.name} has stopped reporting`,
        detail: `No report for ${durationText(box.since_report_s)}. The box may be off, or its fleet agent stopped.`,
      });
    }
    if (box.state === 'never_reported') {
      alerts.push({
        key: `never:${box.name}`,
        tone: 'warn',
        title: `${box.name} has never reported`,
        detail: 'It has a token, and no report has arrived. Check its fleet agent.',
      });
    }

    // Readings from a silent box are its last, not its current condition.
    const report = box.state === 'reporting' ? box.last_report : null;
    if (!report) continue;

    for (const runner of report.runners) {
      const name = runnerName(runner.unit);
      if (runner.listener === 'not_running') {
        alerts.push({
          key: `stopped:${runner.unit}`,
          tone: 'danger',
          title: `${name}'s runner service is ${runner.active_state ?? 'not running'}`,
          detail: `${box.name} is reporting, and this runner cannot take jobs.`,
        });
      }
      const quietFor = runner.last_line_at ? (now - Date.parse(runner.last_line_at)) / 1000 : Infinity;
      if (runner.listener === 'no_connection' && quietFor >= NO_CONNECTION_ALERT_S) {
        alerts.push({
          key: `disconnected:${runner.unit}`,
          tone: 'danger',
          title: `${name} has no connection to GitHub`,
          detail:
            `Its service is running, but it holds no connection to GitHub and has logged nothing for ` +
            `${Number.isFinite(quietFor) ? durationText(quietFor) : 'as long as its log goes back'}. It will not get jobs.`,
        });
      }
    }

    const disk = share(report.disk_used_gb, report.disk_total_gb);
    if (disk !== null && disk > DISK_WARN_SHARE) {
      alerts.push({
        key: `disk:${box.name}`,
        tone: 'warn',
        title: `${box.name}'s disk is ${Math.round(disk * 100)}% full`,
        // A box with no Docker has no prune to run, so it gets no prune advice.
        detail: hasDocker(report)
          ? `Docker holds ${report.docker_gb} GB. The weekly prune may need running now.`
          : 'This box runs no Docker, so there is no prune to run. Free space on it by hand.',
      });
    }
    if (report.cpu_temp_c !== null && report.cpu_temp_c > CPU_HOT_C) {
      alerts.push({
        key: `hot:${box.name}`,
        tone: 'danger',
        title: `${box.name}'s CPU is at ${Math.round(report.cpu_temp_c)} °C`,
        detail: 'Check its vents and fan before it throttles jobs.',
      });
    }
  }

  for (const ups of boxes?.ups ?? []) {
    const l = ups.last;
    const label = ups.model ? `The UPS (${ups.model})` : 'The UPS';
    const line = shutdownLine(ups);
    const left = `${l.charge_pct === null ? 'Charge not reported' : `${pct(l.charge_pct)} charge`}${l.runtime_s === null ? '' : `, about ${durationText(l.runtime_s)} left`}`;
    if (ups.state === 'silent') {
      if (ups.since_report_s < SILENT_ALERT_S) continue;
      alerts.push(
        l.on_battery
          ? {
              key: `ups-silent:${ups.box}:${ups.name}`,
              tone: 'danger',
              title: `${label} went quiet on battery`,
              detail: `Last heard from ${ups.box} ${durationText(ups.since_report_s)} ago at ${left}. The boxes have most likely shut down, and come back on their own when the power does.`,
            }
          : {
              key: `ups-stale:${ups.box}:${ups.name}`,
              tone: 'warn',
              title: `UPS data is stale`,
              detail: `No UPS reading from ${ups.box} for ${durationText(ups.since_report_s)}, so a power cut would not show here. Check NUT on ${ups.box} (upsc ${ups.name}@localhost), or the box itself.`,
            },
      );
      continue;
    }
    if (l.on_battery) {
      alerts.push({
        key: `ups-battery:${ups.box}:${ups.name}`,
        tone: 'danger',
        title: l.low_battery ? `${label} battery is low: the boxes are shutting down` : `${label} is on battery`,
        detail: l.low_battery
          ? `${left}. Every box on it halts now, and starts again when the power is back.`
          : `${left}.${line ? ` Every box on it shuts down at ${line}.` : ''}`,
      });
    } else if (l.low_battery) {
      alerts.push({
        key: `ups-low:${ups.box}:${ups.name}`,
        tone: 'warn',
        title: `${label} is recharging below the shutdown line`,
        detail: `The power is back, at ${l.charge_pct === null ? 'a charge it did not report' : `${pct(l.charge_pct)} charge`}. Another cut before it recharges past ${line ?? 'the low line'} shuts the boxes down at once.`,
      });
    }
  }

  const stuck = (queue?.jobs ?? []).filter((j) => j.for_our_runners && j.stuck);
  if (stuck.length > 0) {
    alerts.push({
      key: 'stuck',
      tone: 'danger',
      title: `${count(stuck.length)} job${stuck.length === 1 ? ' is' : 's are'} stuck waiting for your runners`,
      detail: stuck.map((j) => `${j.repo} · ${j.job_name}`).join(', '),
    });
  }

  // Faults before warnings, in the order found.
  return [...alerts.filter((a) => a.tone === 'danger'), ...alerts.filter((a) => a.tone === 'warn')];
}
